import { useSubscriptionRequiredModal } from '~/composables/useSubscriptionRequiredModal';
import { flash } from '~/composables/flash';
import type { NitroFetchOptions } from 'nitropack';
import type { FetchError } from 'ofetch';

interface ApiResponse<T> {
  data: Ref<T>;
  messages?: Ref<ApiMessage[]>;
  refresh: () => Promise<void>;
  status: Ref<'idle' | 'pending' | 'success' | 'error'>;
  clear?: () => void;
}

interface ApiMessage {
  type: 'error' | 'success';
  title: string;
  description: string | null;
  details?: string[];
}

interface Api {
  get<T>(url: string, opts?: { params?: Record<string, unknown> }): Promise<ApiResponse<T>>;
  post<T>(url: string, body?: Record<string, unknown> | FormData): Promise<ResponseBody<T>>;
  put<T>(url: string, body: unknown): Promise<ResponseBody<T>>;
  patch<T>(url: string, body: unknown): Promise<ResponseBody<T>>;
  delete<T>(url: string, body?: unknown): Promise<ResponseBody<T>>;
}

const toastColor = (type: 'error' | 'success' | 'warning' | 'info') => {
  switch (type) {
    case 'error':
    case 'success':
    case 'warning':
      return type;
    default:
      return 'primary';
  }
};
const toastIcon = (type: 'error' | 'success' | 'warning' | 'info') => {
  switch (type) {
    case 'error':
      return 'heroicons:x-circle';
    case 'success':
      return 'heroicons:check-circle';
    case 'warning':
      return 'heroicons:exclamation-triangle';
    default:
      return 'heroicons:check-circle';
  }
};
// Shared response handlers
const handleResponseMessages = (messages?: ApiMessage[]) => {
  messages?.forEach((message) => {
    if (message.type === 'error') {
      flash(message.type, {
        title: message.title,
        description: message.description ?? '',
        details: message.details,
      });
    } else {
      const details = message.details?.length ? '\n' + message.details.join('\n') : '';
      useToast().add({
        title: message.title,
        description: (message.description ?? '') + details,
        icon: toastIcon(message.type),
        color: toastColor(message.type),
        ui: { description: 'whitespace-pre-line' },
      });
    }
  });
};

const handleResponseError = (
  status: number,
  statusText: string,
  data?: {
    paymentDetails?: { messageKey: string; productType: string };
    messages?: ApiMessage[];
    violations?: [];
  },
) => {
  // 401: handled by retry logic
  if (status === 401) return;

  // 402: Payment Required
  if (status === 402) {
    useSubscriptionRequiredModal().value.messageKey = data!.paymentDetails?.messageKey;
    useSubscriptionRequiredModal().value.productType = data!.paymentDetails?.productType as SubscriptionProductType;
    useSubscriptionRequiredModal().value.show = true;
    return;
  }

  // 422: Validation errors handled by forms
  if (status === 422 && data?.violations) return;

  // API returned explicit error messages
  if (data?.messages) {
    handleResponseMessages(data.messages);
    return;
  }

  // Generic error fallback
  if (status >= 500) {
    const nuxtApp = useNuxtApp();
    flash('exception', {
      title: nuxtApp.$i18n.t('general.error.title'),
      description: nuxtApp.$i18n.t('general.error.description', { status, statusText }),
    });
  }
};

export const useApi = (): Api => {
  const config = useRuntimeConfig();
  const { token } = useAuth();
  const { locale } = useNuxtApp().$i18n;

  const getHeaders = () => ({
    Authorization: 'Basic Yml0bGFkZW46a3VuZGVuY2VudGVy',
    'Api-Authorization': token.value ?? '',
    'Accept-Language': locale.value,
  });

  // GET requests using $fetch (can be called anywhere, including event handlers)
  const fetchGet = async <T>(request: string, opts?: { params?: Record<string, unknown> }): Promise<ApiResponse<T>> => {
    const data = ref<T>(null as T);
    const messages = ref<ApiMessage[]>([]);
    const status = ref<'idle' | 'pending' | 'success' | 'error'>('idle');

    const doFetch = async (isRetry = false) => {
      status.value = 'pending';
      messages.value = [];

      try {
        const response = await $fetch<ResponseBody<T>>(request, {
          baseURL: config.public.baseURL,
          method: 'GET',
          headers: getHeaders(),
          params: opts?.params,
          async onResponse({ response: res }) {
            messages.value = (res._data?.messages as ApiMessage[] | undefined) || [];
            if (res.ok) {
              handleResponseMessages(res._data?.messages);
            }
          },
          async onResponseError({ response: res }) {
            handleResponseError(res.status, res.statusText, res._data);
          },
        });

        data.value = response.data as T;
        status.value = 'success';
      } catch (err: unknown) {
        const fetchError = err as FetchError;

        // Handle 401 with token refresh and retry
        if (fetchError?.response?.status === 401 && !isRetry) {
          const { refresh: authRefresh } = useAuth();
          try {
            await authRefresh();
            return doFetch(true);
          } catch {
            // Refresh failed - AuthRefreshHandler will handle logout
          }
        }

        status.value = 'error';
        throw err;
      }
    };

    await doFetch();

    const clear = () => {
      data.value = null as T;
      messages.value = [];
      status.value = 'idle';
    };

    return {
      data: data as Ref<T>,
      messages,
      status,
      refresh: () => doFetch(),
      clear,
    };
  };

  // POST/PUT/DELETE/PATCH requests using $fetch (promise-based)
  const send = async <T>(request: string, opts: NitroFetchOptions<string>, isRetry = false): Promise<ResponseBody<T>> => {
    try {
      return await $fetch(request, {
        baseURL: config.public.baseURL,
        headers: getHeaders(),
        ...opts,
        async onResponse({ response }: { response: { ok: boolean; _data: { messages?: ApiMessage[] } } }) {
          if (response.ok) {
            handleResponseMessages(response._data?.messages);
          }
        },
        async onResponseError({ response }: { response: { _data?: { messages?: ApiMessage[]; violations?: []; detail?: string } } & Response }) {
          handleResponseError(response.status, response.statusText, response._data);
        },
      } as NitroFetchOptions<string>);
    } catch (error: unknown) {
      const fetchError = error as FetchError;
      if (fetchError?.response?.status === 401 && !isRetry) {
        const { refresh } = useAuth();
        try {
          await refresh();
          return send<T>(request, opts, true);
        } catch {
          // Refresh failed - AuthRefreshHandler will handle logout
        }
      }
      throw error;
    }
  };

  const get = async <T>(request: string, opts?: { params?: Record<string, unknown> }): Promise<ApiResponse<T>> => {
    return fetchGet(request, opts);
  };

  const post = async <T>(request: string, body?: Record<string, unknown> | FormData): Promise<ResponseBody<T>> => {
    flash('clear');
    return send(request, { method: 'POST', body });
  };

  const put = async <T>(request: string, body?: Record<string, unknown>): Promise<ResponseBody<T>> => {
    flash('clear');
    return send(request, { method: 'PUT', body });
  };

  const del = async <T>(request: string, body?: Record<string, unknown>): Promise<ResponseBody<T>> => {
    flash('clear');
    return send(request, { method: 'DELETE', body });
  };

  const patch = async <T>(request: string, body?: Record<string, unknown>): Promise<ResponseBody<T>> => {
    flash('clear');
    return send(request, { method: 'PATCH', body });
  };

  return { post, get, put, delete: del, patch };
};
